/** Shared Normal Trading rules — Buy / Hold / Sell classification */

import { formatIstDateTime } from './ist-datetime';
import { setChargeProductFromOrderType } from './equity-delivery-charges';

export let PER_SHARE_PROFIT = 0.50;
/** HOLD full target ₹/sh — Master holdProfitRs (independent of BUY). */
export let HOLD_PROFIT_TARGET_RS = 0.50;
export let PER_SHARE_PARTIAL_MIN = 0.30;
export let PER_SHARE_PARTIAL_MAX = 0.50;
/** HOLD soft profit band after soft-wait (Master). */
export let HOLD_ESCAPE_MIN_RS = 0.10;
export let HOLD_ESCAPE_MAX_RS = 1000;
/** HOLD stop-loss TOTAL net ₹ = NET×qty (Master holdStopLossRs). 0.10 = −₹0.10 total. */
export let HOLD_STOP_LOSS_RS = 0.10;
/** Master band max total net ₹ (informational on UI; backend clamps). */
export let HOLD_STOP_LOSS_MAX_RS = 5;
export let HOLD_STOP_LOSS_PCT = 0;
/** Minutes on HOLD before soft band can sell. */
export let HOLD_SOFT_AFTER_MIN = 0;
export let HOLD_STOP_AFTER_MIN = 0;
/** SELL tab seconds before Sold Today (Master). */
export let SELL_TAB_AUTO_SOLD_SEC = 10;
/** SELL tab: total loss (move×qty) −₹min…−₹max → Sold Today immediately. */
export let SELL_TAB_NET_LOSS_MIN_RS = 5;
export let SELL_TAB_NET_LOSS_MAX_RS = 10;
export const PER_SHARE_NEAR_TARGET_MIN = 0.40;
export const PER_SHARE_STOP = 0.01;
export let MAX_HOLD_MINUTES = 10;
/** V1: capital from Master (default ₹50,000) — not unlimited unless Master says so */
export let UNLIMITED_CAPITAL = false;
export let MIN_SHARE_PRICE = 100;
export let MAX_SHARE_PRICE = 2000;
export let DAILY_POOL_CAP = 50000;
export let LIVE_SLOT_COUNT = 5;
export let SLOT_BUDGET = Math.round((DAILY_POOL_CAP / LIVE_SLOT_COUNT) * 100) / 100;
export const MIN_BUY_WIN_SCORE = 80;
export const MIN_WIN_RATE_PCT = 80;
export const MAX_WIN_RATE_PCT = 100;
/** Default share count fallback — live V1 qty = slot cash ÷ price, clamped 1–100. */
export let STOCK_QTY = 100;
export let MIN_BUY_QTY = 1;
export let MAX_BUY_QTY = 100;
export let FILL_INTERVAL_SEC = 60;
/** Min estimated net ₹ after CNC charges before auto-sell (0 = legacy). */
export let MIN_NET_PROFIT_RS = 0;
export let DAILY_NET_TARGET_RS = 1000;
/** true = BUY stays until profit (pipeline fill must not vanish). */
export let BUY_STICKY_UNTIL_PROFIT = true;
export let PEAK_PULLBACK_RS = 1;
export let PEAK_PULLBACK_ARM_RS = 0.5;
export let NEWS_GATE_ENABLED = false;
export let STRATEGY_MODE = 'locked-v1';

/** Apply V1 Master settings from API — affects Normal Trading V1 screen only. */
export function applyV1MasterSettings(master) {
  if (!master || typeof master !== 'object') return;
  setChargeProductFromOrderType(master.orderType || 'MIS');
  if (master.buyProfitRs != null) PER_SHARE_PROFIT = Number(master.buyProfitRs);
  // Do not raise BUY target to fee floor — Master buyProfitRs is the SELL latch
  if (master.holdProfitRs != null) HOLD_PROFIT_TARGET_RS = Number(master.holdProfitRs);
  if (master.holdPartialMinRs != null) PER_SHARE_PARTIAL_MIN = Number(master.holdPartialMinRs);
  if (master.holdPartialMaxRs != null) PER_SHARE_PARTIAL_MAX = Number(master.holdPartialMaxRs);
  if (master.holdEscapeMinRs != null) HOLD_ESCAPE_MIN_RS = Number(master.holdEscapeMinRs);
  if (master.holdEscapeMaxRs != null) HOLD_ESCAPE_MAX_RS = Number(master.holdEscapeMaxRs);
  if (master.holdSoftAfterMin != null) HOLD_SOFT_AFTER_MIN = 0;
  if (master.holdStopAfterMin != null) HOLD_STOP_AFTER_MIN = 0;
  HOLD_SOFT_AFTER_MIN = 0;
  HOLD_STOP_AFTER_MIN = 0;
  if (master.holdStopLossMinRs != null) {
    /* applied via holdStopLossRs below */
  }
  if (master.holdStopLossRs != null) HOLD_STOP_LOSS_RS = Math.max(0, Number(master.holdStopLossRs));
  else if (master.holdStopLossMinRs != null) HOLD_STOP_LOSS_RS = Math.max(0, Number(master.holdStopLossMinRs));
  else HOLD_STOP_LOSS_RS = 0.10;
  if (master.holdStopLossMaxRs != null) HOLD_STOP_LOSS_MAX_RS = Math.max(HOLD_STOP_LOSS_RS, Number(master.holdStopLossMaxRs));
  if (master.holdStopLossPct != null) HOLD_STOP_LOSS_PCT = Math.max(0, Number(master.holdStopLossPct));
  else HOLD_STOP_LOSS_PCT = 0;
  if (master.sellTabAutoSoldSec != null) SELL_TAB_AUTO_SOLD_SEC = Math.max(0, Number(master.sellTabAutoSoldSec));
  if (master.maxHoldMinutes != null) MAX_HOLD_MINUTES = Number(master.maxHoldMinutes);
  if (master.minBuyQty != null) MIN_BUY_QTY = Math.max(1, Math.min(100, Number(master.minBuyQty)));
  if (master.maxBuyQty != null) MAX_BUY_QTY = Math.max(MIN_BUY_QTY, Math.min(100, Number(master.maxBuyQty)));
  if (master.stockQty != null) STOCK_QTY = Number(master.stockQty);
  else STOCK_QTY = MAX_BUY_QTY;
  if (master.slotsMax != null) LIVE_SLOT_COUNT = Number(master.slotsMax);
  if (master.fillIntervalSec != null) FILL_INTERVAL_SEC = Number(master.fillIntervalSec);
  if (master.capitalAmount != null) DAILY_POOL_CAP = Number(master.capitalAmount);
  if (master.minNetProfitPerTradeRs != null) MIN_NET_PROFIT_RS = Number(master.minNetProfitPerTradeRs);
  if (master.dailyNetTargetRs != null) DAILY_NET_TARGET_RS = Number(master.dailyNetTargetRs);
  else if (master.dailyTargetRs != null) DAILY_NET_TARGET_RS = Number(master.dailyTargetRs);
  if (master.buyStickyUntilProfit === false) BUY_STICKY_UNTIL_PROFIT = false;
  else if (master.buyStickyUntilProfit === true) BUY_STICKY_UNTIL_PROFIT = true;
  else BUY_STICKY_UNTIL_PROFIT = true;
  if (master.peakPullbackRs != null) PEAK_PULLBACK_RS = Math.max(0, Number(master.peakPullbackRs));
  if (master.peakPullbackArmRs != null) PEAK_PULLBACK_ARM_RS = Math.max(0.05, Number(master.peakPullbackArmRs));
  NEWS_GATE_ENABLED = master.newsGateEnabled === true;
  STRATEGY_MODE = String(master.strategyMode || 'locked-v1').toLowerCase() === 'tom-hoggard'
    ? 'tom-hoggard'
    : 'locked-v1';
  if (master.holdStopLossMaxRs != null) HOLD_STOP_LOSS_MAX_RS = Math.max(HOLD_STOP_LOSS_RS, Number(master.holdStopLossMaxRs));
  UNLIMITED_CAPITAL = master.unlimitedCapital === true;
  SLOT_BUDGET = UNLIMITED_CAPITAL
    ? Number.MAX_SAFE_INTEGER
    : Math.round((DAILY_POOL_CAP / Math.max(1, LIVE_SLOT_COUNT)) * 100) / 100;
  const band = master.activeBand || master.priceBands?.[master.priceBand];
  if (band) {
    if (band.min != null) MIN_SHARE_PRICE = Number(band.min);
    if (band.max != null) MAX_SHARE_PRICE = Number(band.max);
  }
}

/** Display half-rupee target as ₹0.50 (not ₹0.5). */
export function fmtRsPerShare(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return `₹${PER_SHARE_PROFIT.toFixed(2)}`;
  return `₹${v.toFixed(2)}`;
}

export function normalizeStockQty() {
  return STOCK_QTY;
}

export function getLiveMove(slot, liveMap = {}) {
  const stock = slot?.stock || slot?.pendingStock;
  const botPrice = slot?.botPrice ?? slot?.pendingPrice ?? slot?.entryPrice ?? null;
  const liveQ = stock ? liveMap[stock] : null;
  const currentPrice = liveQ?.price ?? slot?.currentPrice ?? null;
  if (botPrice != null && currentPrice != null) {
    return Math.round((Number(currentPrice) - Number(botPrice)) * 100) / 100;
  }
  const move = slot?.botMove ?? slot?.perShareMove ?? slot?.move;
  return move != null ? Math.round(Number(move) * 100) / 100 : null;
}

export function holdMsFromOpenedAt(openedAt) {
  if (!openedAt) return 0;
  return Math.max(0, Date.now() - new Date(openedAt).getTime());
}

export function isTimedOut(slot, maxHoldMin = MAX_HOLD_MINUTES) {
  if (slot?.timeoutRotateSell || slot?.timeoutPartialSell) return true;
  if (slot?.timeoutWaitingProfit) return true;
  if (!slot?.openedAt) return false;
  return holdMsFromOpenedAt(slot.openedAt) >= maxHoldMin * 60 * 1000;
}

export function isWithinTenMin(slot, maxHoldMin = MAX_HOLD_MINUTES) {
  return slot?.openedAt ? !isTimedOut(slot, maxHoldMin) : true;
}

export function isProfitSellHit(slot, profitRs = PER_SHARE_PROFIT, liveMap = {}) {
  const profit = Number(profitRs) || PER_SHARE_PROFIT;
  if (slot?.sellNow) return true;
  const move = getLiveMove(slot, liveMap);
  return move != null && move >= profit - 0.004;
}

export function isHoldSecondTimedOut(holdSince, maxHoldMin = HOLD_SOFT_AFTER_MIN, nowMs = Date.now()) {
  if (!holdSince) return false;
  const waitMin = maxHoldMin != null ? maxHoldMin : HOLD_SOFT_AFTER_MIN;
  return holdMsFromOpenedAt(holdSince) >= waitMin * 60 * 1000
    || Math.max(0, nowMs - new Date(holdSince).getTime()) >= waitMin * 60 * 1000;
}

export function isHoldSoftWindowOpen(holdSince, nowMs = Date.now(), softAfterMin = HOLD_SOFT_AFTER_MIN) {
  const soft = Number(softAfterMin);
  if (soft <= 0) return true;
  if (!holdSince) return false;
  return holdMsFromOpenedAt(holdSince) >= soft * 60 * 1000
    || Math.max(0, nowMs - new Date(holdSince).getTime()) >= soft * 60 * 1000;
}

/** Late HOLD stage (Master holdStopAfterMin) — any profit → SELL. */
export function isHoldLateStageOpen(holdSince, nowMs = Date.now(), stopAfterMin = HOLD_STOP_AFTER_MIN) {
  const after = Number(stopAfterMin);
  if (after <= 0) return false;
  if (!holdSince) return false;
  return holdMsFromOpenedAt(holdSince) >= after * 60 * 1000
    || Math.max(0, nowMs - new Date(holdSince).getTime()) >= after * 60 * 1000;
}

export function qualifiesHoldEscape(move, escapeMin = HOLD_ESCAPE_MIN_RS, escapeMax = HOLD_ESCAPE_MAX_RS, profitRs = HOLD_PROFIT_TARGET_RS) {
  if (move == null) return false;
  if (move >= profitRs - 0.004) return false;
  return move >= escapeMin - 0.004 && move <= escapeMax + 0.004;
}

/**
 * HOLD → SELL: full Master holdProfitRs only (soft/late waits removed).
 */
export function qualifiesHoldTabSell(move, holdSince, nowMs = Date.now(), opts = {}) {
  void holdSince;
  void nowMs;
  if (move == null) return false;
  const profitTarget = Number(opts.profitRs ?? HOLD_PROFIT_TARGET_RS);
  return move >= profitTarget - 0.004;
}

export function isPartialProfitSell(slot, liveMap = {}) {
  if (isProfitSellHit(slot, PER_SHARE_PROFIT, liveMap)) return false;
  const move = getLiveMove(slot, liveMap);
  return qualifiesHoldEscape(move);
}

export function isStopCapSell() {
  return false;
}

export function isStopBreachSell() {
  return false;
}

export function isStopAtLevel() {
  return false;
}

export function isStopHit() {
  return false;
}

export function isNearTargetWithinTenMin(slot, liveMap = {}, profitRs = PER_SHARE_PROFIT) {
  if (isTimedOut(slot)) return false;
  if (isProfitSellHit(slot, profitRs, liveMap)) return false;
  const move = getLiveMove(slot, liveMap);
  const target = Number(profitRs) || PER_SHARE_PROFIT;
  return move != null
    && move >= PER_SHARE_NEAR_TARGET_MIN - 0.004
    && move < target - 0.004;
}

export function classifySellTab(slot, liveMap = {}, profitRs = PER_SHARE_PROFIT) {
  if (isProfitSellHit(slot, profitRs, liveMap)) {
    return { sell: true, kind: 'urgent', reason: `+₹${(getLiveMove(slot, liveMap) ?? 0).toFixed(2)}/sh ≥ ${fmtRsPerShare(profitRs)}/sh — sell on Groww now` };
  }
  if (isNearTargetWithinTenMin(slot, liveMap, profitRs)) {
    const move = getLiveMove(slot, liveMap);
    return {
      sell: true,
      kind: 'near_target',
      reason: `Within 10 min · +₹${(move ?? 0).toFixed(2)}/sh (₹0.40–₹0.50 zone) — sell on Groww`
    };
  }
  if (isPartialProfitSell(slot, liveMap)) {
    const move = getLiveMove(slot, liveMap);
    return {
      sell: true,
      kind: 'urgent',
      reason: `HOLD → +₹${(move ?? 0).toFixed(2)}/sh (₹${HOLD_ESCAPE_MIN_RS.toFixed(2)}–₹${HOLD_ESCAPE_MAX_RS.toFixed(0)}) — sell on Groww now`
    };
  }
  return { sell: false };
}

export function classifyHoldTab(slot, liveMap = {}, profitRs = PER_SHARE_PROFIT) {
  if (!slot?.live || !slot?.stock) return false;
  if (classifySellTab(slot, liveMap, profitRs).sell) return false;
  return true;
}

export function meetsBuyWinRate(slot, minScore = MIN_BUY_WIN_SCORE, analysis = []) {
  if (slot?.winRate != null) {
    const w = Number(slot.winRate);
    if (w >= minScore && w <= MAX_WIN_RATE_PCT) return true;
  }
  let score = slot?.rs1PredictScore ?? slot?.rs1Score ?? 0;
  if ((!score || score < minScore) && slot?.score != null && Number(slot.score) <= MAX_WIN_RATE_PCT) {
    score = slot.score;
  }
  if ((!score || score < minScore) && slot?.pendingStock && analysis?.length) {
    const sk = String(slot.pendingStock).replace(/\.NS$/i, '');
    const row = analysis.find((a) => String(a.symbol || '').replace(/\.NS$/i, '') === sk);
    score = row?.rs1PredictScore ?? (Number(row?.score) <= MAX_WIN_RATE_PCT ? row?.score : 0) ?? score;
  }
  if (Number(score) >= minScore && Number(score) <= MAX_WIN_RATE_PCT) return true;
  const displayed = formatWinRate({ ...slot, rs1PredictScore: score });
  return displayed != null && displayed >= minScore && displayed <= MAX_WIN_RATE_PCT;
}

/** Live AI Volatile BUY tab — only stocks with displayed win 80–100%. */
export function qualifiesLiveBuyWin(item, analysis = []) {
  if (!meetsBuyWinRate(item, MIN_BUY_WIN_SCORE, analysis)) return false;
  const displayed = formatWinRate(item);
  return displayed != null && displayed >= MIN_WIN_RATE_PCT && displayed <= MAX_WIN_RATE_PCT;
}

export function formatWinRate(slot) {
  if (slot?.winRate != null && Number.isFinite(Number(slot.winRate))) {
    return Math.min(
      MAX_WIN_RATE_PCT,
      Math.max(MIN_WIN_RATE_PCT, Math.round(Number(slot.winRate)))
    );
  }
  const score = slot?.rs1PredictScore ?? slot?.score ?? slot?.rs1Score;
  if (score == null) return null;
  const raw = Math.round(Number(score) * 0.82);
  return Math.min(MAX_WIN_RATE_PCT, Math.max(MIN_WIN_RATE_PCT, raw));
}

export function formatIstClock(iso) {
  if (!iso) return null;
  try {
    return formatIstDateTime(iso);
  } catch {
    return null;
  }
}

/** Countdown until 10 min window ends (Rule 2 — BUY → HOLD). */
export function formatHoldRemaining(openedAt, maxHoldMin = MAX_HOLD_MINUTES, nowMs = Date.now()) {
  if (!openedAt) return null;
  const holdMs = Math.max(0, nowMs - new Date(openedAt).getTime());
  const maxMs = maxHoldMin * 60 * 1000;
  const remainingMs = Math.max(0, maxMs - holdMs);
  const m = Math.floor(remainingMs / 60000);
  const s = Math.floor((remainingMs % 60000) / 1000);
  const timedOut = remainingMs <= 0;
  return {
    timedOut,
    holdMs,
    remainingMs,
    label: timedOut ? '10 min done' : (m > 0 ? `${m}m ${s}s left` : `${s}s left`)
  };
}

export function formatHoldElapsed(openedAt, nowMs = Date.now()) {
  if (!openedAt) return null;
  const holdMs = Math.max(0, nowMs - new Date(openedAt).getTime());
  const m = Math.floor(holdMs / 60000);
  const s = Math.floor((holdMs % 60000) / 1000);
  return {
    holdMs,
    label: m > 0 ? `Held ${m}m ${s}s` : `Held ${s}s`
  };
}

/** Attach Rule 2 timing labels for BUY / HOLD cards (multi-stage HOLD). */
export function attachHoldTiming(item, tab = 'buy', nowMs = Date.now()) {
  const maxHoldMin = item.maxHoldMinutes ?? MAX_HOLD_MINUTES;
  const softAfterMin = item.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN;
  const stopAfterMin = item.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN;
  const openedAt = item.openedAt
    || (item.previewOpenedAt ? new Date(item.previewOpenedAt).toISOString() : null);
  const holdSince = item.holdSince || openedAt;
  const timerAt = tab === 'hold' ? holdSince : openedAt;
  if (!openedAt) {
    if (item.pending) {
      return {
        ...item,
        timeLeft: 'Waiting for Groww mirror — timer starts when pick loads',
        countdown: '--:--'
      };
    }
    return item;
  }

  const fmtClock = (msLeft) => {
    const rem = Math.max(0, msLeft);
    const m = Math.floor(rem / 60000);
    const s = Math.floor((rem % 60000) / 1000);
    return rem <= 0 ? '00:00' : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  if (tab === 'hold') {
    const holdMsVal = holdSince ? Math.max(0, nowMs - new Date(holdSince).getTime()) : 0;
    const softAfterMs = Math.max(0, Number(softAfterMin) || 0) * 60 * 1000;
    const stopAfterMs = Math.max(softAfterMs, (Number(stopAfterMin) || 0) * 60 * 1000);
    const softLeft = Math.max(0, softAfterMs - holdMsVal);
    const stopLeft = Math.max(0, stopAfterMs - holdMsVal);
    const elapsed = formatHoldElapsed(holdSince || openedAt, nowMs);
    const softMin = HOLD_ESCAPE_MIN_RS;
    const softMax = HOLD_ESCAPE_MAX_RS;
    const fullRs = HOLD_PROFIT_TARGET_RS;

    let holdStage = 'soft_wait';
    let countdown = fmtClock(softLeft);
    let holdLabel = softAfterMs > 0 ? 'SOFT WAIT' : 'SOFT OPEN';
    let holdStageHint = `Full +₹${Number(fullRs).toFixed(2)}/sh anytime → SELL`;
    let holdTimerUrgent = false;

    if (softAfterMs <= 0 || softLeft <= 0) {
      if (stopAfterMs > 0 && stopLeft > 0) {
        holdStage = 'soft_open';
        countdown = fmtClock(stopLeft);
        holdLabel = 'SOFT OPEN';
        holdStageHint = `Soft +₹${Number(softMin).toFixed(2)}–₹${Number(softMax).toFixed(2)}/sh → SELL · late stage in ${countdown}`;
        holdTimerUrgent = stopLeft <= 60 * 1000;
      } else {
        holdStage = 'late';
        countdown = '00:00';
        holdLabel = 'LATE STAGE';
        holdStageHint = 'Any profit → SELL tab · no stop-loss';
        holdTimerUrgent = true;
      }
    } else {
      holdStageHint = `Soft +₹${Number(softMin).toFixed(2)}–₹${Number(softMax).toFixed(2)}/sh in ${countdown} · full +₹${Number(fullRs).toFixed(2)} anytime`;
      holdTimerUrgent = softLeft <= 60 * 1000;
    }

    return {
      ...item,
      openedAt,
      holdSince,
      maxHoldMinutes: maxHoldMin,
      holdSoftAfterMin: softAfterMin,
      holdStopAfterMin: stopAfterMin,
      countdown,
      buyTimerUrgent: holdTimerUrgent,
      holdElapsedLabel: elapsed?.label,
      holdRemainingLabel: holdStageHint,
      holdLabel,
      holdStage,
      holdStageHint,
      softCountdown: softLeft > 0 ? fmtClock(softLeft) : '00:00',
      stopCountdown: stopLeft > 0 ? fmtClock(stopLeft) : '00:00',
      buyTimeLabel: item.buyTimeLabel || formatIstClock(openedAt)
    };
  }

  const remaining = formatHoldRemaining(timerAt, maxHoldMin, nowMs);
  const elapsed = formatHoldElapsed(timerAt, nowMs);
  const buyTimeLabel = item.buyTimeLabel || formatIstClock(openedAt);
  const remMs = remaining?.remainingMs ?? 0;
  const countdown = remaining?.timedOut ? '00:00' : fmtClock(remMs);
  const buyTimerUrgent = !remaining?.timedOut && remMs > 0 && remMs <= 60 * 1000;

  return {
    ...item,
    openedAt,
    maxHoldMinutes: maxHoldMin,
    countdown,
    buyTimerUrgent,
    holdRemainingLabel: remaining?.label,
    holdElapsedLabel: elapsed?.label,
    buyTimeLabel
  };
}

/** SELL tab → Sold Today countdown (Master sellTabAutoSoldSec). */
export function attachSellTiming(item, nowMs = Date.now()) {
  const waitSec = Math.max(
    0,
    Number(item.sellWaitSec != null ? item.sellWaitSec : SELL_TAB_AUTO_SOLD_SEC) || 0
  );
  const since = item.sellTabSince || item.sellSince || null;
  if (!(waitSec > 0)) {
    return {
      ...item,
      sellCountdown: '0',
      sellTimerLabel: 'Sold Today now',
      sellTimerUrgent: true,
      countdown: '00:00',
      holdLabel: 'SELL NOW'
    };
  }
  let left = waitSec;
  if (since) {
    const started = Date.parse(since);
    if (Number.isFinite(started)) {
      const elapsedSec = Math.max(0, (nowMs - started) / 1000);
      left = Math.max(0, Math.ceil(waitSec - elapsedSec));
    }
  }
  const mm = Math.floor(left / 60);
  const ss = left % 60;
  const countdown = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return {
    ...item,
    sellCountdown: String(left),
    sellTimerLabel: left > 0 ? `Sold Today in ${left}s` : 'Sold Today now',
    sellTimerUrgent: left <= 10,
    sellWaitSec: waitSec,
    countdown,
    holdLabel: left > 0 ? 'SELL TIMER' : 'SELL NOW',
    holdRemainingLabel: left > 0
      ? `Beep · sell on Groww · auto Sold Today in ${left}s`
      : 'Moving to Sold Today'
  };
}
